import React from 'react';
import {useTranslation} from 'react-i18next';

import type {LogoSizeModifier} from '@pexip/components';
import {Icon, IconTypes} from '@pexip/components';

import {InMeetingInputControl} from '../../InputControl/InMeetingInputControl.view';
import {MuteInputTooltipContent} from '../../MuteInputTooltip/MuteInputTooltipContent.view';
import {TestId} from '../../../../test/testIds';

export const AudioButton: React.FC<
    Omit<
        React.ComponentProps<typeof InMeetingInputControl>,
        'aria-label' | 'icon'
    > & {
        canMute?: boolean;
        iconSize: LogoSizeModifier;
        isMicrophoneMuted: boolean;
        onAudioBtnClick: () => void;
        tooltipClassName?: string;
    }
> = ({
    isMicrophoneMuted,
    onAudioBtnClick,
    iconSize,
    canMute = true,
    tooltipClassName,
    ...props
}) => {
    const {t} = useTranslation();
    const muteMicText = isMicrophoneMuted
        ? t('media.unmute-microphone', 'Unmute microphone')
        : t('media.mute-microphone', 'Mute microphone');

    const testId = isMicrophoneMuted
        ? TestId.ButtonMeetingAudioInputMuted
        : TestId.ButtonMeetingAudioInput;

    return (
        <InMeetingInputControl
            canMute={canMute}
            isActive={isMicrophoneMuted}
            onClick={onAudioBtnClick}
            tooltipPosition="top"
            tooltipText={
                <MuteInputTooltipContent
                    isAudioMuted={isMicrophoneMuted}
                    muteText={muteMicText}
                />
            }
            tooltipClassName={tooltipClassName}
            data-testid={testId}
            aria-label={muteMicText}
            icon={
                isMicrophoneMuted ? (
                    <Icon
                        source={IconTypes.IconMicrophoneOff}
                        size={iconSize}
                    />
                ) : (
                    <Icon source={IconTypes.IconMicrophoneOn} size={iconSize} />
                )
            }
            {...props}
        />
    );
};

export type AudioButtonProps = React.ComponentProps<typeof AudioButton>;
